/**
 * M6 — Alerta de renovación: cuotas recurrentes ACTIVAS sin fecha límite de
 * contrato (ni nº de cuotas ni fecha fin). Se muestra en la ficha del cliente
 * y en la de la oportunidad para recordar que hay que revisar la renovación.
 */

import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BellRing } from 'lucide-react'
import { api } from '../../lib/api'
import type { RecurringInvoiceListResponse } from '@toga/shared'

interface Props {
  clientId?: string
  opportunityId?: string
}

export default function RenewalAlert({ clientId, opportunityId }: Props) {
  const { t } = useTranslation('billing')
  const [names, setNames] = useState<string[]>([])

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const params = new URLSearchParams({ status: 'ACTIVE' })
        if (opportunityId) params.set('opportunityId', opportunityId)
        else if (clientId) params.set('clientId', clientId)
        else return
        const resp = await api.get<RecurringInvoiceListResponse>(
          `/recurring-invoices?${params.toString()}`
        )
        if (cancelled) return
        setNames(
          resp.items
            .filter((r) => r.maxOccurrences === null && r.endsAt === null)
            .map((r) => r.name)
        )
      } catch {
        // Silencioso: la alerta es informativa, no bloquea la ficha.
        if (!cancelled) setNames([])
      }
    })()
    return () => {
      cancelled = true
    }
  }, [clientId, opportunityId])

  if (names.length === 0) return null

  return (
    <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2.5 flex items-start gap-2.5">
      <BellRing size={16} className="text-amber-700 dark:text-amber-300 mt-0.5 shrink-0" />
      <div className="min-w-0 text-sm">
        <p className="font-medium text-amber-800 dark:text-amber-200">
          {t('renewalAlert.title', { count: names.length })}
        </p>
        <p className="text-xs text-amber-700 dark:text-amber-300 mt-0.5">
          {t('renewalAlert.body', { names: names.join(', ') })}
        </p>
      </div>
    </div>
  )
}
